'use client'

import * as React from 'react'

import { cn } from '../cn'
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './dialog'

type SimpleDialogMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'

interface SimpleDialogProps {
  open: boolean
  /** Called when the user dismisses the dialog (esc, overlay click, close button). */
  onClose: () => void
  title: string
  description?: string
  /** Max-width preset for the dialog content on sm+ screens. Default: "lg". */
  maxWidth?: SimpleDialogMaxWidth
  /** Render as a full-screen dialog (no max-width, no close button). */
  fullscreen?: boolean
  /** Extra className forwarded to DialogContent. */
  className?: string
  children: React.ReactNode
}

const maxWidthClasses: Record<SimpleDialogMaxWidth, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
}

/**
 * Controlled, single-component dialog wrapper around the composable Radix
 * Dialog primitives. Use when the caller just wants `open/onClose/title` and
 * doesn't need the full DialogTrigger/DialogContent/DialogHeader composition.
 *
 * Overrides the upstream Dialog's `overflow-hidden` so that absolutely-
 * positioned children (autocomplete dropdowns, custom popovers, etc.)
 * are not clipped. This matches the old road-warrior Dialog convention.
 *
 * For dialogs that need internal scrolling for very long content, use the
 * composable Dialog primitives directly with DialogBody.
 */
function SimpleDialog({
  open,
  onClose,
  title,
  description,
  maxWidth = 'lg',
  fullscreen,
  className,
  children,
}: SimpleDialogProps) {
  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        fullscreen={fullscreen}
        className={cn(
          // Mobile (base): keep upstream overflow so the full-screen dialog scrolls.
          // Desktop (sm+): override to overflow-visible so dropdowns are not
          // clipped, and remove max-h so the dialog grows with content.
          'sm:overflow-visible sm:max-h-none',
          !fullscreen && maxWidthClasses[maxWidth],
          className,
        )}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogBody className="sm:overflow-visible">{children}</DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export { SimpleDialog, type SimpleDialogMaxWidth, type SimpleDialogProps }
